import type { UIMessage } from "ai";
import { z } from "zod";
import {
  BRAND_PROFILE_EDIT_EVIDENCE_SOURCE_TYPES,
  geographicMarketSchema,
  getBrandAssistantProfilePreviewResolutionsFromParts,
  onboardingAudienceProfileDraftsV1Schema,
  type BrandProfileEditEvidenceSourceType,
  type GeographicMarket,
  type OnboardingAudienceProfileDraftV1,
} from "@runcheetah/shared";

export type AssistantBrandProfileCompetitorTupleValue = [string, string][];
export type AssistantBrandProfileStructuredObjectValue = Record<string, unknown>[];

export type AssistantBrandProfileEditPreviewValue =
  | string
  | string[]
  | GeographicMarket[]
  | AssistantBrandProfileCompetitorTupleValue
  | AssistantBrandProfileStructuredObjectValue;

export type AssistantBrandProfileEditPreviewChange = {
  fieldPath: string;
  label: string;
  previous: AssistantBrandProfileEditPreviewValue | null;
  next: AssistantBrandProfileEditPreviewValue;
  rationale: string | null;
  sourceUrl?: string | null;
  sourceType?: BrandProfileEditEvidenceSourceType | null;
  confidence?: number | null;
};

export type AssistantBrandProfileEditPreviewReference = {
  id: string;
  summary: string | null;
  changes: AssistantBrandProfileEditPreviewChange[];
  audienceProfiles?: OnboardingAudienceProfileDraftV1[];
  confirmationSource?: "chatbot_onboarding";
};

export type AssistantBrandProfileEditPreviewResolutionStatus = "applied" | "dismissed";

export type AssistantBrandProfileEditPreviewDigest = {
  highlights: string[];
  hiddenChangeCount: number;
};

export type AssistantOnboardingProfilePreviewGate = {
  status: "missing" | "pending" | "resolved";
  resolvedAtMessageIndex: number | null;
};

const geographicMarketsValueSchema = geographicMarketSchema.array();
const competitorTupleValueSchema = z.array(z.tuple([z.string(), z.string()]));
const structuredObjectArrayValueSchema = z.array(z.record(z.string(), z.unknown()));
const assistantBrandProfileEditEvidenceSourceTypeSchema = z.enum(
  BRAND_PROFILE_EDIT_EVIDENCE_SOURCE_TYPES,
);
const PREVIEW_VALUE_SUMMARY_MAX_LENGTH = 90;

export function getAssistantBrandProfileEditPreviewReferences(
  message: Pick<UIMessage, "parts">,
): AssistantBrandProfileEditPreviewReference[] {
  const references: AssistantBrandProfileEditPreviewReference[] = [];

  for (const [index, part] of message.parts.entries()) {
    const candidate = part as {
      type?: string;
      state?: string;
      toolCallId?: string;
      output?: {
        summary?: unknown;
        changes?: unknown[];
        audienceProfiles?: unknown;
        confirmationSource?: unknown;
      };
    };

    if (
      candidate.type !== "tool-preview_brand_profile_edits" ||
      candidate.state !== "output-available" ||
      !Array.isArray(candidate.output?.changes)
    ) {
      continue;
    }

    const changes = candidate.output.changes.flatMap((change) => {
      const parsed = parseAssistantBrandProfileEditPreviewChange(change);
      return parsed ? [parsed] : [];
    });

    const summary =
      typeof candidate.output.summary === "string" ? candidate.output.summary.trim() : "";
    const confirmationSource =
      candidate.output.confirmationSource === "chatbot_onboarding"
        ? "chatbot_onboarding"
        : undefined;
    const audienceProfiles = confirmationSource
      ? onboardingAudienceProfileDraftsV1Schema.safeParse(candidate.output.audienceProfiles)
      : null;
    references.push({
      id: typeof candidate.toolCallId === "string" ? candidate.toolCallId : `preview-${index}`,
      summary: summary || null,
      changes,
      ...(audienceProfiles?.success ? { audienceProfiles: audienceProfiles.data } : {}),
      confirmationSource,
    });
  }

  return references;
}

export function getAssistantBrandProfileEditPreviewResolutionStatuses(
  messages: readonly Pick<UIMessage, "parts">[],
): Map<string, AssistantBrandProfileEditPreviewResolutionStatus> {
  const statuses = new Map<string, AssistantBrandProfileEditPreviewResolutionStatus>();

  for (const message of messages) {
    for (const resolution of getBrandAssistantProfilePreviewResolutionsFromParts(message.parts)) {
      statuses.set(resolution.previewId, resolution.status);
    }
  }

  return statuses;
}

export function getLatestOnboardingBrandProfileEditPreviewId(
  messages: readonly Pick<UIMessage, "parts">[],
): string | null {
  let latestPreviewId: string | null = null;

  for (const message of messages) {
    for (const reference of getAssistantBrandProfileEditPreviewReferences(message)) {
      if (reference.confirmationSource === "chatbot_onboarding" && reference.changes.length > 0) {
        latestPreviewId = reference.id;
      }
    }
  }

  return latestPreviewId;
}

export function getAssistantOnboardingProfilePreviewGate(
  messages: readonly Pick<UIMessage, "parts">[],
): AssistantOnboardingProfilePreviewGate {
  let latestOnboardingPreviewId: string | null = null;
  const resolutionMessageIndexes = new Map<string, number>();

  messages.forEach((message, messageIndex) => {
    for (const reference of getAssistantBrandProfileEditPreviewReferences(message)) {
      if (reference.confirmationSource === "chatbot_onboarding" && reference.changes.length > 0) {
        latestOnboardingPreviewId = reference.id;
      }
    }

    for (const resolution of getBrandAssistantProfilePreviewResolutionsFromParts(message.parts)) {
      resolutionMessageIndexes.set(resolution.previewId, messageIndex);
    }
  });

  if (!latestOnboardingPreviewId) {
    return { status: "missing", resolvedAtMessageIndex: null };
  }

  const resolvedAtMessageIndex = resolutionMessageIndexes.get(latestOnboardingPreviewId);
  if (resolvedAtMessageIndex === undefined) {
    return { status: "pending", resolvedAtMessageIndex: null };
  }

  return { status: "resolved", resolvedAtMessageIndex };
}

function parseAssistantBrandProfileEditPreviewChange(
  value: unknown,
): AssistantBrandProfileEditPreviewChange | null {
  if (!value || typeof value !== "object") {
    return null;
  }

  const change = value as {
    fieldPath?: unknown;
    label?: unknown;
    previous?: unknown;
    next?: unknown;
    rationale?: unknown;
    sourceUrl?: unknown;
    sourceType?: unknown;
    confidence?: unknown;
  };
  const next = parseAssistantBrandProfileEditPreviewValue(change.next);
  if (typeof change.fieldPath !== "string" || typeof change.label !== "string" || next === null) {
    return null;
  }

  const previous =
    change.previous === null || change.previous === undefined
      ? null
      : parseAssistantBrandProfileEditPreviewValue(change.previous);
  if (change.previous !== null && change.previous !== undefined && previous === null) {
    return null;
  }

  const parsedSourceType = assistantBrandProfileEditEvidenceSourceTypeSchema.safeParse(
    change.sourceType,
  );
  const sourceUrl =
    typeof change.sourceUrl === "string" && change.sourceUrl.trim()
      ? change.sourceUrl.trim()
      : change.sourceUrl === null
        ? null
        : undefined;
  const confidence =
    typeof change.confidence === "number" && change.confidence >= 0 && change.confidence <= 1
      ? change.confidence
      : change.confidence === null
        ? null
        : undefined;
  const sourceType = parsedSourceType.success
    ? parsedSourceType.data
    : change.sourceType === null
      ? null
      : undefined;

  return {
    fieldPath: change.fieldPath,
    label: change.label,
    previous,
    next,
    rationale: typeof change.rationale === "string" ? change.rationale : null,
    ...(sourceUrl !== undefined ? { sourceUrl } : {}),
    ...(sourceType !== undefined ? { sourceType } : {}),
    ...(confidence !== undefined ? { confidence } : {}),
  };
}

function parseAssistantBrandProfileEditPreviewValue(
  value: unknown,
): AssistantBrandProfileEditPreviewValue | null {
  if (typeof value === "string") {
    return value;
  }

  if (!Array.isArray(value)) {
    return null;
  }

  if (value.every((entry) => typeof entry === "string")) {
    return value;
  }

  const geographicMarkets = geographicMarketsValueSchema.safeParse(value);
  if (geographicMarkets.success) {
    return geographicMarkets.data;
  }

  const competitorTuples = competitorTupleValueSchema.safeParse(value);
  if (competitorTuples.success) {
    return competitorTuples.data;
  }

  const structuredObjects = structuredObjectArrayValueSchema.safeParse(value);
  return structuredObjects.success ? structuredObjects.data : null;
}

export function buildAssistantBrandProfileEditPreviewDigest(
  reference: Pick<AssistantBrandProfileEditPreviewReference, "changes">,
  options?: { maxHighlights?: number },
): AssistantBrandProfileEditPreviewDigest {
  const maxHighlights = options?.maxHighlights ?? 4;
  const highlights = reference.changes.slice(0, maxHighlights).map((change) => {
    const next = formatAssistantBrandProfileEditPreviewValueSummary(change.next);
    return `${change.label}: ${next}`;
  });

  return {
    highlights,
    hiddenChangeCount: Math.max(reference.changes.length - highlights.length, 0),
  };
}

export function formatAssistantBrandProfileEditPreviewValueSummary(
  value: AssistantBrandProfileEditPreviewValue | null,
): string {
  if (value === null || (typeof value === "string" && value.trim().length === 0)) {
    return "Not set";
  }

  if (typeof value === "string") {
    return truncatePreviewSummary(value.replace(/\s+/g, " ").trim());
  }

  if (value.length === 0) {
    return "Empty";
  }

  if (isAssistantBrandProfileCompetitorTupleValue(value)) {
    return summarizePreviewList(value.map(([name]) => name));
  }

  if (isAssistantBrandProfileGeographicMarketValue(value)) {
    return summarizePreviewList(value.map((market) => market.label));
  }

  if (isAssistantBrandProfileStructuredObjectValue(value)) {
    return summarizePreviewList(value.map(formatStructuredObjectPreviewLabel));
  }

  return summarizePreviewList(value);
}

export function isAssistantBrandProfileCompetitorTupleValue(
  value: AssistantBrandProfileEditPreviewValue | null,
): value is AssistantBrandProfileCompetitorTupleValue {
  return Array.isArray(value) && value.length > 0 && value.every(isCompetitorTupleEntry);
}

function isCompetitorTupleEntry(value: unknown): value is [string, string] {
  return (
    Array.isArray(value) &&
    value.length === 2 &&
    typeof value[0] === "string" &&
    typeof value[1] === "string"
  );
}

function isAssistantBrandProfileGeographicMarketValue(
  value: AssistantBrandProfileEditPreviewValue,
): value is GeographicMarket[] {
  return (
    Array.isArray(value) &&
    value.length > 0 &&
    value.every((entry) => typeof entry === "object" && entry !== null && "label" in entry)
  );
}

function isAssistantBrandProfileStructuredObjectValue(
  value: AssistantBrandProfileEditPreviewValue,
): value is AssistantBrandProfileStructuredObjectValue {
  return (
    Array.isArray(value) &&
    value.length > 0 &&
    value.every((entry) => typeof entry === "object" && entry !== null && !Array.isArray(entry))
  );
}

export function formatStructuredObjectPreviewLabel(value: {
  name?: unknown;
  label?: unknown;
}): string {
  const namedValue = typeof value.name === "string" ? value.name.trim() : "";
  if (namedValue) {
    return namedValue;
  }

  const labeledValue = typeof value.label === "string" ? value.label.trim() : "";
  if (labeledValue) {
    return labeledValue;
  }

  return "Structured value";
}

function summarizePreviewList(items: readonly string[]) {
  const visible = items.slice(0, 2);
  const suffix = items.length > visible.length ? ` +${items.length - visible.length}` : "";
  return truncatePreviewSummary(`${visible.join(", ")}${suffix}`);
}

function truncatePreviewSummary(value: string) {
  if (value.length <= PREVIEW_VALUE_SUMMARY_MAX_LENGTH) {
    return value;
  }

  return `${value.slice(0, PREVIEW_VALUE_SUMMARY_MAX_LENGTH - 1).trimEnd()}...`;
}
